import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/axios';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Users, RefreshCw } from 'lucide-react';

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

const buildLogoUrl = (logo) => {
  if (!logo) return null;
  if (logo.startsWith('http')) return logo;
  return `${BACKEND_ORIGIN}${logo}`;
};

export const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPartners = useCallback(async (retries = 3) => {
    setLoading(true);
    setError(null);
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const { data } = await API.get('/partners', {
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
          params: { _t: Date.now() },
        });
        setPartners(Array.isArray(data) ? data : []);
        setLoading(false);
        return;
      } catch (err) {
        console.warn(`Attempt ${attempt}/${retries} failed to load partners:`, err.message);
        if (attempt === retries) {
          setError('Gagal memuat data partner. Silakan coba lagi.');
        } else {
          await new Promise((r) => setTimeout(r, 500 * attempt));
        }
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  return (
    <div className="App">
      <Header />

      <section className="py-24 container mx-auto px-4 min-h-screen">
        <div className="text-center mb-12">
          <Users className="inline-block text-[#FFD60A] mb-4" size={64} />
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight" data-testid="partners-title">
            Partner Kami
          </h1>
          <p className="text-gray-400 text-lg">Dipercaya oleh brand-brand terbaik</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-white text-xl">Loading...</div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400 text-lg mb-4">{error}</p>
            <button
              onClick={() => fetchPartners()}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="retry-partners-btn"
            >
              <RefreshCw size={18} /> Coba Lagi
            </button>
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">Belum ada partner</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {partners.map((partner) => {
              const logoUrl = buildLogoUrl(partner.logo);
              const CardTag = partner.website_url ? 'a' : 'div';
              const linkProps = partner.website_url
                ? { href: partner.website_url, target: '_blank', rel: 'noopener noreferrer' }
                : {};
              return (
                <CardTag
                  key={partner.id}
                  {...linkProps}
                  className="glass-card p-6 text-center hover:scale-105 transition-transform block"
                  data-testid={`partner-card-${partner.id}`}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={partner.name}
                      className="w-full h-32 object-contain mb-4"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center mb-4 bg-white/5 rounded-lg">
                      <span className="text-white/50 font-bold text-4xl">{partner.name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  <h3 className="text-white font-bold mb-2">{partner.name}</h3>
                  {partner.description && (
                    <p className="text-gray-400 text-sm">{partner.description}</p>
                  )}
                  {partner.category && (
                    <div className="mt-3">
                      <span className="text-xs bg-white/10 px-3 py-1 rounded-full text-gray-300">
                        {partner.category}
                      </span>
                    </div>
                  )}
                </CardTag>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};
